import { Link, RouteComponentProps } from '@reach/router';
import { LessonsApi, Unit } from 'api/lessonsApi';
import LessonPageStatus from 'components/lessons/LessonPageStatus';
import { useLessonPrint } from 'components/lessons/LessonPrint';
import {
  AccessButton,
  closedAccessButton,
  LessonsRouteParams,
  openFileInNewTab,
  unitPath,
  useLessonsPage,
} from 'components/lessons/lessonsHelpers';
import LessonTop from 'components/lessons/LessonTop';
import Page from 'components/shared/Page';
import React, { useState } from 'react';

type Props = RouteComponentProps<LessonsRouteParams>;

const LessonUnit: React.FC<Props> = (props) => {
  const result = useLessonsPage<Unit>(props, (query) =>
    LessonsApi.getUnit(query)
  );
  const subjectsId = result && typeof result !== 'string' ? result.subject.subjectsID : null;
  const { checkQuota, popup, errorMessage } = useLessonPrint(subjectsId);
  const [bFileError, setBFileError] = useState(false);

  if (result === null || typeof result === 'string') {
    return <LessonPageStatus status={result} />;
  }

  const { subject, query, details: unit } = result;
  const path = unitPath(subject.internalCode, unit.keyStage.strippedName, unit.activity.strippedName);
  const viewText = unit.access.bFullAccess ? 'View' : 'View Sample';

  const card = (
    key: number | string,
    detail: string,
    button: AccessButton,
    onClick?: () => void,
    extraDetail?: string | null
  ) => {
    const content = (
      <div className="LessonCardTable">
        <div className="LessonCardYear">{unit.keyStageName}</div>
        <div className="LessonCardDesc">
          <div className="LessonCardTitle">{unit.activity.name}</div>
          <div className="LessonCardDetail">{detail}</div>
          {extraDetail && <div className="LessonCardDetail">{extraDetail}</div>}
        </div>
        <div className="LessonCardButton">
          <span className={`Btn ${button.bDisabled ? 'BtnGrey' : 'BtnOrange'}`}>
            {button.text}
          </span>
        </div>
      </div>
    );

    if (onClick) {
      return (
        <button key={key} type="button" className="LessonCard" onClick={onClick}>
          {content}
        </button>
      );
    }
    return button.href ? (
      <Link key={key} to={button.href} className="LessonCard">
        {content}
      </Link>
    ) : (
      <div key={key} className={`LessonCard${button.bDisabled ? ' Disabled' : ''}`}>
        {content}
      </div>
    );
  };

  const lessonButton = (bCanOpen: boolean, href: string): AccessButton => {
    if (bCanOpen) return { text: viewText, href, bDisabled: false };
    return unit.access.bFullAccess
      ? { text: 'Subscribers Only', href: null, bDisabled: true }
      : closedAccessButton(unit.access);
  };

  const openKnowledgeOrganiser = (resourcesId: number) => {
    setBFileError(false);
    let fileWindow = window.open('', '_blank');
    const getUrl = () => LessonsApi.getResourceFileUrl(subject.subjectsID, resourcesId, false);
    checkQuota(
      `${window.location.pathname}/knowledge-organiser`,
      () => {
        openFileInNewTab(getUrl, fileWindow ?? window.open('', '_blank')).then(
          (bOpened) => setBFileError(!bOpened)
        );
        fileWindow = null;
      },
      () => {
        fileWindow?.close();
        fileWindow = null;
      }
    );
  };

  const scheme = unit.schemeOfWork;
  const knowledgeOrganiser = unit.knowledgeOrganiser;

  return (
    <Page bypassPageAccess>
      <div className="LessonPage">
        <LessonTop activity={unit.activity} keyStageName={unit.keyStageName} backPath="/" />
        <div className="WidthContent LessonSection">
          {!scheme ? (
            <p>No Unit of Work details found</p>
          ) : (
            <div className="LessonCards">
              {card(
                'scheme',
                scheme.name,
                lessonButton(scheme.bCanOpen, `${path}/schemeofwork?years=${query.years}`)
              )}
              {knowledgeOrganiser &&
                (scheme.bCanOpen
                  ? card('knowledge', 'Knowledge Organiser', { text: viewText, href: null, bDisabled: false }, () =>
                      openKnowledgeOrganiser(knowledgeOrganiser.resourcesId)
                    )
                  : card('knowledge', 'Knowledge Organiser', lessonButton(false, '')))}
            </div>
          )}
          {errorMessage}
          {bFileError && <p className="ErrorLabel">An error occurred, please try again.</p>}
        </div>
        <div className="WidthContent LessonSection">
          <h2>Lesson Plans</h2>
          {unit.lessons.length === 0 ? (
            <p>No Lesson plans available for this activity</p>
          ) : (
            <div className="LessonCards">
              {unit.lessons.map((lesson) =>
                card(
                  lesson.lessonsId,
                  `Lesson ${lesson.lessonNumber}`,
                  lessonButton(lesson.bCanOpen, `${path}/${lesson.lessonNumber}?years=${query.years}`),
                  undefined,
                  lesson.theme
                )
              )}
            </div>
          )}
        </div>
      </div>
      {popup}
    </Page>
  );
};

export default LessonUnit;
